/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Modal, Space } from 'antd';
import Draggable, { DraggableBounds, DraggableData, DraggableEvent } from 'react-draggable';
import { useTranslation } from 'react-i18next';
import { UserOutlined } from '@ant-design/icons';
import { useStore } from '../stores/common.ts';
import * as Selector from '../stores/selector';
import { setMessage } from '../helpers.tsx';
import { MAX_SCREEN_NAME_LENGTH, screenNameOf, setScreenName } from '../screenName.ts';

export interface ScreenNameModalProps {
  open: boolean;
  onClose: () => void;
}

// Asks an anonymous user what to call them. Shown once right after an anonymous
// sign-in (skippable — a name is a courtesy to a teacher, not a condition of
// using the app) and reachable afterwards from the avatar menu and the account
// page, since the moment someone realizes they need a name is usually the
// moment they join a class, not the moment they sign in.
const ScreenNameModal = React.memo(({ open, onClose }: ScreenNameModalProps) => {
  const language = useStore(Selector.language);
  const user = useStore(Selector.user);

  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [dragEnabled, setDragEnabled] = useState(false);
  const [bounds, setBounds] = useState<DraggableBounds>({ left: 0, top: 0, bottom: 0, right: 0 } as DraggableBounds);
  const dragRef = useRef<HTMLDivElement | null>(null);

  const { t } = useTranslation();
  const lang = useMemo(() => ({ lng: language }), [language]);

  // Seed with whatever name they already chose, so reopening this edits rather
  // than starts over. The placeholder "Anonymous" is not a name — screenNameOf
  // filters it out and the field opens empty.
  useEffect(() => {
    if (open) setName(screenNameOf(user) ?? '');
  }, [open, user.displayName]);

  const onStart = (event: DraggableEvent, uiData: DraggableData) => {
    if (dragRef.current) {
      const { clientWidth, clientHeight } = window.document.documentElement;
      const targetRect = dragRef.current.getBoundingClientRect();
      setBounds({
        left: -targetRect.left + uiData.x,
        right: clientWidth - (targetRect.right - uiData.x),
        top: -targetRect.top + uiData.y,
        bottom: clientHeight - (targetRect.bottom - uiData.y),
      });
    }
  };

  const onOk = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      if (await setScreenName(name)) {
        setMessage('success', t('accountSettingsPanel.ScreenNameSet', lang));
        onClose();
      } else {
        setMessage('error', t('accountSettingsPanel.CannotSetScreenName', lang));
      }
    } catch (e) {
      setMessage('error', t('accountSettingsPanel.CannotSetScreenName', lang) + ': ' + e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      width={420}
      title={
        <div
          style={{ width: '100%', cursor: 'move' }}
          onMouseOver={() => setDragEnabled(true)}
          onMouseOut={() => setDragEnabled(false)}
        >
          <UserOutlined /> {t('accountSettingsPanel.ScreenName', lang)}
        </div>
      }
      open={open}
      footer={[
        <Button key="Skip" onClick={onClose}>
          {t('word.Skip', lang)}
        </Button>,
        <Button key="OK" type="primary" loading={busy} disabled={!name.trim()} onClick={onOk}>
          {t('word.Save', lang)}
        </Button>,
      ]}
      onCancel={onClose}
      maskClosable={false}
      modalRender={(modal) => (
        <Draggable nodeRef={dragRef} disabled={!dragEnabled} bounds={bounds} onStart={onStart}>
          <div ref={dragRef}>{modal}</div>
        </Draggable>
      )}
    >
      <Space direction="vertical" size={8} style={{ width: '100%', paddingTop: '8px' }}>
        <span style={{ fontSize: '13px', color: '#57606a' }}>{t('accountSettingsPanel.ScreenNameNote', lang)}</span>
        <Input
          value={name}
          autoFocus
          maxLength={MAX_SCREEN_NAME_LENGTH}
          placeholder={t('accountSettingsPanel.ScreenNamePlaceholder', lang)}
          onChange={(e) => setName(e.target.value)}
          onPressEnter={onOk}
        />
      </Space>
    </Modal>
  );
});

export default ScreenNameModal;
